// src/pages/PartDetailsContent.jsx
import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import useSelectableCardController from "../controllers/useSelectableCardController";
import shareIcon from "../assets/images/share.png";
import axios from "axios";
import { pushAddToCart } from "../utils/gtmAddToCart";

import { BASE_IMAGE_URL, API_BASE_URL } from "../utils/api";
import usePartDetailsController from "../controllers/usePartDetailsController";

import tamaraIcon from "../assets/images/Tamara1.png"; // (kept for future use)
import madaIcon from "../assets/images/mada.png";       // (kept for future use)
import desc from "../assets/images/Frame.png";
import car from "../assets/images/Directions car.png";
import deliveryIcon from "../assets/images/deliveryIcon.png";
import price from "../assets/images/Frame (1).png";
import logo from "../assets/images/logo.png";
import currencyGreen from "../assets/images/currency_white_1.png";

import "../styles/partDetails.css";
import "../styles/mobile-partDetails.css";

import Footer from "../components/Footer.jsx";
import MobileFooterSheet from "./MobileFooterSheet.jsx";

// -------- guest cart helpers (same as in card) --------
const LOCAL_CART_KEY = "cart"; // array of part IDs
function safeGetLocalCart() {
  try {
    if (typeof window === "undefined") return [];
    const raw = localStorage.getItem(LOCAL_CART_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
function saveLocalCart(arr,analyticsItem) {
  try {
    if (typeof window === "undefined") return;
    localStorage.setItem(LOCAL_CART_KEY, JSON.stringify(arr));
    // Only push when full item info is provided
    if (analyticsItem) {
      pushAddToCart(analyticsItem); // { item_name, item_id, price, quantity }
    }
  } catch { }
}
function isInLocalCart(partId) {
  const cart = safeGetLocalCart();
  return cart.some((x) => String(x) === String(partId));
}
function addToLocalCart(partId, analyticsItem) {
  const cart = new Set(safeGetLocalCart().map(String));
  cart.add(String(partId));
  saveLocalCart(Array.from(cart), analyticsItem);
}
function removeFromLocalCart(partId) {
  const next = safeGetLocalCart().filter((x) => String(x) !== String(partId));
  saveLocalCart(next);
}
// ------------------------------------------------------

export default function PartDetailsContent({
  part,
  selectedColor,
  brandName,
  modelName,
  yearValue,
  categoryValue,
  quantity,
  onBack,
  onBuyNow,
  onLogoClick,
  onCartClick, // optional; if not provided we'll fallback to /cart navigation
  setCurrentView
}) {
  const c = usePartDetailsController({
    part,
    brandName,
    modelName,
    yearValue,
    categoryValue,
    onBuyNow,
  });

  // Reuse the same share/wishlist logic from the card controller
  const {
    shareRef,
    showShareBox,
    setShowShareBox,
    partUrl,
    inWishlist,
    wishLoading,
    onWishlistClick,
  } = useSelectableCardController(part,setCurrentView);


  const [copied, setCopied] = useState(false);

  const partId = part?.id;

  // ----- isMobile (to switch icon/text like TopBar) -----
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );
  const [isLaptop, setIsLaptop] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 1024 : true
  );
  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      setIsMobile(w <= 768);
      setIsLaptop(w >= 1024);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const [zoomOpen, setZoomOpen] = useState(false);
  // const openZoom = () => { if (isLaptop && c.mainImage) setZoomOpen(true); };
  const openZoom = () => {setZoomOpen(true); };
  const closeZoom = () => setZoomOpen(false);
  useEffect(() => {
    if (!zoomOpen) return;
    const onKey = (e) => { if (e.key === "Escape") closeZoom(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoomOpen]);

  // ----- cart count (guest vs authed) -----
  const [cartCount, setCartCount] = useState(0);
  const token =
    typeof window !== "undefined" ? localStorage.getItem("token") : null;

  const readGuestCount = () => {
    try {
      const raw = localStorage.getItem("cart");
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.length : 0;
    } catch {
      return 0;
    }
  };

  // Normalize various API shapes to an array of carts
  const extractCarts = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (payload?.data && typeof payload.data === "object") return [payload.data];
    if (payload && typeof payload === "object" && Array.isArray(payload.items)) return [payload];
    return [];
  };

  // Count items:
  // - prefer cart.total_quantity if > 0
  // - otherwise sum items[].quantity (0/undefined -> 1)
  const deriveCount = (payload) => {
    const carts = extractCarts(payload);
    return carts.reduce((sum, cart) => {
      const tq = Number(cart?.total_quantity);
      if (tq > 0) return sum + tq;
      const items = Array.isArray(cart?.items) ? cart.items : [];
      const itemsSum = items.reduce((s, it) => {
        const q = Number(it?.quantity);
        return s + (q > 0 ? q : 1);
      }, 0);
      return sum + itemsSum;
    }, 0);
  };

  const fetchAuthedCount = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/carts/pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (data && data.status === false) throw new Error("Unauthenticated");
      setCartCount(deriveCount(data));
    } catch {
      setCartCount(readGuestCount());
    }
  };

  useEffect(() => {
    if (!token) {
      setCartCount(readGuestCount());
      return;
    }
    fetchAuthedCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // react to localStorage changes across tabs (guest only)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "cart" && !token) setCartCount(readGuestCount());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [token]);

  // allow manual recount
  useEffect(() => {
    const handler = () => {
      if (token) fetchAuthedCount();
      else setCartCount(readGuestCount());
    };
    window.addEventListener("cart:recount", handler);
    return () => window.removeEventListener("cart:recount", handler);
  }, [token]);

  // local (guest) cart state for this part
  const [guestInCart, setGuestInCart] = React.useState(() =>
    isInLocalCart(partId)
  );

  // sync when part changes or storage changes from another tab
  React.useEffect(() => {
    setGuestInCart(isInLocalCart(partId));
    const onStorage = (e) => {
      if (e.key === LOCAL_CART_KEY) setGuestInCart(isInLocalCart(partId));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [partId]);

  // intercept guest modal attempt from controller -> store locally instead
  const interceptedRef = React.useRef(false);
  React.useEffect(() => {
    if (c.showGuestModal && !interceptedRef.current) {
      interceptedRef.current = true;
      const analyticsItem = {
        item_id: partId,            // part id
        item_name: part?.name,       // part name
        price: part?.price,           // part price (number/string ok)
        quantity: 1             // this card adds one item
      };
      addToLocalCart(partId, analyticsItem); // <-- pushes event via saveLocalCart
      setGuestInCart(true);
      c.closeGuestModal?.();
      setTimeout(() => (interceptedRef.current = false), 150);
      // recount badge
      try { window.dispatchEvent(new Event("cart:recount")); } catch { }
    }
  }, [c.showGuestModal, partId, c]);

  // unified handler for add/remove (this part)
  const onPartCartClick = (e) => {
    e.stopPropagation();

    // إذا غير متوفر لا تفعل شيء
    if (isOutOfStock) return;

    // if already in any cart, do nothing (button will be disabled anyway)
    if (c.cartItemId || guestInCart) return;

    // otherwise add (auth → API, guest → intercepted to localStorage)
    return c.handleAddToCart?.(e);
  };

  const isOutOfStock =
    typeof quantity !== "undefined"
      ? Number(quantity) < 1
      : Number(part?.quantity ?? 0) < 1;

  const isInAnyCart = !!c.cartItemId || guestInCart;
  const cartBtnLabel = isOutOfStock
    ? "نفذت الكمية"
    : c.checkingCart
      ? "..."
      : isInAnyCart
        ? "تمت الاضافة"
        : "إضافة للسلة";


  // navigate to cart when pressing header cart button
  const goCart = () => {
    if (onCartClick) return onCartClick();
    // fallback
    try {
      // If your app uses setCurrentView elsewhere, feel free to dispatch a custom event instead.
      window.location.assign("/cart");
    } catch { }
  };

  return (
    <div className="part-details-page" dir="rtl">
      {/* Topbar */}
      <div className="topbar">
        <motion.button
          className="back-pill"
          onClick={() => (typeof onBack === "function" ? onBack() : goCart())}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.98 }}
          style={{ position: "absolute", height: 40, top: 30, left: 80 }}
        >
          <span>ارجع للخلف</span>
        </motion.button>

        {/* Cart button placed to the RIGHT of the back button */}
        <button
          className="cart-icon-btn-details has-badge"
          onClick={goCart}
          aria-label="سلة التسوق"
          title="سلة التسوق"
          type="button"
          style={{ position: "absolute", top: 30, left: 230 }}
        >
          {isMobile ? <i className="bi bi-cart3" /> : "سلة التسوق"}
          {Number(cartCount) > 0 && <span className="cart-badge cart-badge-details">{cartCount}</span>}
        </button>

        <h1></h1>

        <img
          src={logo}
          alt="PARTECH"
          onClick={onLogoClick}
          role="button"
          tabIndex={0}
          style={{
            position: "absolute",
            height: "40px",
            top: "30px",
            right: "80px",
            cursor: "pointer",
            outline: "none",
          }}
        />
      </div>


      <h1 className="screen-title mb-3">{part?.name}</h1>

      {/* Main */}
      <main
        className="container py-4"
        style={{ position: "absolute", top: "20%", left: "10%" }}
      >
        <div className="row g-4">
          {/* Gallery */}
          <div className="col-md-4">
            <section className="pd-gallery">
              <div
                className="pd-hero"
                onClick={openZoom}
                // role={isLaptop ? "button" : undefined}
                role="button"
                title={isLaptop ? "تكبير الصورة" : undefined}
                style={{cursor:"zoom-in"}}
                // style={{ cursor: isLaptop ? "zoom-in" : "default" }}
              >
                {c.mainImage ? (
                  <img
                    className="pd-hero-img"
                    src={`${BASE_IMAGE_URL}${c.mainImage}`}
                    alt={part?.name}
                  />
                ) : (
                  <div className="pd-noimg">لا توجد صورة متوفرة</div>
                )}
              </div>

              {Array.isArray(part?.images) && part.images.length > 1 && (
                <div className="pd-thumbs mt-3" aria-label="صور إضافية">
                  {part.images.map((img, idx) => (
                    <button
                      key={idx}
                      className={`pd-thumb ${c.mainImage === img ? "active" : ""}`}
                      onClick={() => c.setMainImage(img)}
                      title={`preview-${idx}`}
                    >
                      <img src={`${BASE_IMAGE_URL}${img}`} alt={`img-${idx}`} />
                    </button>
                  ))}
                </div>
              )}
            </section>
          </div>

          {/* Details */}
          <div className="col-md-8">
            <section className="pd-card">
              <div className="pd-card-inner">
                <div className="pd-meta-list">
                  {/* Vehicle */}
                  {c.compat && (
                    <div className="pd-row">
                      <img src={car} className="pd-ico" alt="تفاصيل السيارة" />
                      <div>
                        <div className="pd-k">تفاصيل السيارة :</div>
                        <div className="pd-v">{c.compat}</div>
                      </div>
                    </div>
                  )}

                  {/* Actions bar: Wishlist + Share (next to details / above Add-to-cart) */}
                  <div
                    className="pd-actions-bar"
                    style={{
                      display: "flex",
                      justifyContent: "end",
                      gap: 22,
                      alignItems: "center",
                      margin: "-63px 25px 12px",
                    }}
                  >
                    {/* Wishlist */}
                    <button
                      className={`wish-btn ${inWishlist ? "active" : ""}`}
                      type="button"
                      aria-label={inWishlist ? "إزالة من المفضلة" : "أضِف إلى المفضلة"}
                      aria-pressed={inWishlist}
                      onClick={onWishlistClick}
                      title={inWishlist ? "إزالة من المفضلة" : "أضِف إلى المفضلة"}
                      disabled={wishLoading}
                      style={{
                        border: "none",
                        background: "transparent",
                        padding: "6px 10px",
                        borderRadius: 10,
                        cursor: "pointer",
                      }}
                    >
                      {/* same heart shape used in card */}
                      <svg width="25" height="25" viewBox="0 0 24 24" fill={inWishlist ? "currentColor" : "none"} stroke="currentColor">
                        <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 22l7.8-8.6 1-1a5.5 5.5 0 0 0 0-7.8z" />
                      </svg>
                    </button>

                    {/* Share */}
                    <div className="pd-share" ref={shareRef} style={{ position: "relative", marginInlineStart: "0" }}>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.97 }}
                        className="pd-icon-btn pd-icon-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowShareBox(v => {
                            const next = !v;
                            if (next) setCopied(false);
                            return next;
                          });
                        }}
                        title="مشاركة"
                        style={{ background: "transparent", borderRadius: 10, padding: 6, border: "none" }}
                      >
                        <img src={shareIcon} alt="share" width={25} />
                      </motion.button>

                      <AnimatePresence>
                        {showShareBox && (
                          <motion.div
                            className="pd-pop"
                            initial={{ opacity: 0, y: -6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -6 }}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              position: "absolute",
                              insetInlineStart: 0,
                              top: "calc(100% + 6px)",
                              minWidth: 260,
                              background: "#1e1e1e",
                              color: "#fff",
                              borderRadius: 10,
                              padding: 10,
                              boxShadow: "0 6px 18px rgba(0,0,0,0.35)",
                              zIndex: 10,
                            }}
                          >
                            <div className="pd-pop-row" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                              <p className="pd-pop-link" title={partUrl} style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", margin: 0 }}>
                                {partUrl}
                              </p>
                              <button
                                className="pd-copy"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(partUrl).then(() => {
                                    setCopied(true);
                                    setTimeout(() => setCopied(false), 2000);
                                  });
                                }}
                                style={{ background: "#CAEA3E", border: "none", borderRadius: 8, padding: "6px 10px", cursor: "pointer" }}
                              >
                                {copied ? "تم النسخ" : "نسخ"}
                              </button>
                            </div>
                            <div className="pd-soc" style={{ display: "flex", gap: 10, marginTop: 8 }}>
                              <a href={`https://www.facebook.com/sharer/sharer.php?u=${partUrl}`} target="_blank" rel="noreferrer" aria-label="Facebook"><i className="bi bi-facebook" /></a>
                              <a href={`https://twitter.com/intent/tweet?url=${partUrl}`} target="_blank" rel="noreferrer" aria-label="Twitter"><i className="bi bi-twitter" /></a>
                              <a href={`https://api.whatsapp.com/send?text=${partUrl}`} target="_blank" rel="noreferrer" aria-label="WhatsApp"><i className="bi bi-whatsapp" /></a>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>


                  {/* Description */}
                  {part?.description && (
                    <div className="pd-row">
                      <img src={desc} className="pd-ico" alt="الوصف" />
                      <div>
                        <div className="pd-k">الوصف:</div>
                        <div className="pd-v">{part.description}</div>
                      </div>
                    </div>
                  )}

                  {/* Price row */}
                  {part?.price && (
                    <div className="pd-row pd-price-inline">
                      <img src={price} className="pd-ico" alt="السعر" />
                      <div className="pd-price-inline-wrap">
                        <div className="pd-k">السعر:</div>

                        <div className="pd-price-amount">
                          <div className="pd-v">
                            <p style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ textDecoration: "line-through", color: "#aaa" }}>
                                {part.market_price ?? "—"}
                              </span>
                              <span style={{ fontWeight: "bold" }}>
                                {part.price ?? "—"}
                              </span>
                              <img src={currencyGreen} alt="SAR" width={30} />
                            </p>
                          </div>
                        </div>

                        <div className="pd-cta-inline">
                          <motion.button
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.98 }}
                            className="pd-btn pd-btn-sm"
                            onClick={(e) => onPartCartClick(e)}
                            disabled={c.checkingCart || isInAnyCart}
                            title={c.checkingCart ? "جاري التحقق..." : undefined}
                          >
                            {cartBtnLabel}
                          </motion.button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Delivery (static wording preserved) */}
                  <div className="pd-row" style={{ marginTop: "-22px" }}>
                    <img src={deliveryIcon} className="pd-ico" alt="تفاصيل السيارة" />
                    <div>
                      <div className="pd-k">تكلفة التوصيل:</div>
                      <div className="pd-v">
                        {part.delivery_price ?? "—"}{" "}
                        <img src={currencyGreen} alt="" width={30} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>

      {zoomOpen && (
        <div
          className="pd-zoom-overlay"
          onClick={closeZoom}
          role="dialog"
          aria-modal="true"
          aria-label="تكبير الصورة"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.8)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
          }}
        >
          <div
            className="pd-zoom-content"
            onClick={(e) => e.stopPropagation()}
            style={{ position: "relative", maxWidth: "90vw", maxHeight: "90vh" }}
          >
            <button
              type="button"
              aria-label="إغلاق"
              onClick={closeZoom}
              style={{
                position: "absolute",
                top: "-44px",
                insetInlineEnd: 0,
                background: "transparent",
                color: "#fff",
                border: "none",
                fontSize: 18,
                cursor: "pointer",
              }}
              title="إغلاق"
            >
              ✕
            </button>

            {c.mainImage ? (
              <img
                src={`${BASE_IMAGE_URL}${c.mainImage}`}
                alt={part?.name}
                style={{
                  display: "block",
                  maxWidth: "90vw",
                  maxHeight: "90vh",
                  width: "auto",
                  height: "auto",
                  objectFit: "contain",
                  borderRadius: 8,
                }}
              />
            ) : (
              <div style={{ color: "#fff" }}>لا توجد صورة</div>
            )}
          </div>
        </div>
      )}

      {/* Desktop footer; mobile bottom sheet */}
      {/* <div className="desktop-only"><Footer /></div> */}
      
    </div>
  );
}
